import { motion } from 'framer-motion'
import ScrollFloat from './ScrollFloat'
import { Database, Code2, Cloud, Server, Layout, Bot, Smartphone, Link2, ShieldCheck } from './SketchIconPack'

const stackCategories = [
  {
    title: 'Frontend',
    icon: Layout,
    description: 'Modern, reactive user interfaces with exceptional performance.',
    technologies: ['React', 'Next.js', 'JavaScript', 'TypeScript', 'Tailwind CSS', 'ShadCN UI', 'Framer Motion', 'Vite']
  },
  {
    title: 'Backend',
    icon: Server,
    description: 'Scalable and secure server-side architecture and APIs.',
    technologies: ['Node.js', 'Express.js', 'NestJS', 'Socket.IO', 'GraphQL', 'REST APIs', 'JWT Authentication']
  },
  {
    title: 'Database',
    icon: Database,
    description: 'Robust data storage, optimization, and real-time synchronization.',
    technologies: ['MongoDB', 'PostgreSQL', 'MySQL', 'Supabase', 'Redis', 'Prisma ORM']
  },
  {
    title: 'Cloud & DevOps',
    icon: Cloud,
    description: 'Automated deployments, containerization, and cloud infrastructure.',
    technologies: ['AWS', 'Vercel', 'Docker', 'Cloudflare', 'GitHub Actions', 'Linux', 'Nginx', 'DigitalOcean']
  },
  {
    title: 'AI & Automation',
    icon: Bot,
    description: 'Intelligent agents, LLMs, and workflow automation systems.',
    technologies: ['OpenAI', 'Gemini', 'Claude', 'LangChain', 'AI Agents', 'RAG Systems', 'Vector Databases', 'n8n', 'Make']
  },
  {
    title: 'Mobile Development',
    icon: Smartphone,
    description: 'Cross-platform mobile applications for iOS and Android.',
    technologies: ['React Native', 'Expo', 'Android', 'iOS']
  },
  {
    title: 'Integrations',
    icon: Link2,
    description: 'Seamless third-party API and payment gateway connections.',
    technologies: ['Razorpay', 'Stripe', 'WhatsApp Business API', 'Google Maps API', 'Twilio']
  },
  {
    title: 'Security',
    icon: ShieldCheck,
    description: 'Enterprise-grade data protection and access control.',
    technologies: ['JWT', 'OAuth 2.0', 'RBAC', 'API Security', 'Data Encryption']
  }
]

export default function TechnologyStack() {
  return (
    <section className="pt-24 pb-8 relative bg-[var(--color-bg-primary)] overflow-hidden">
      <div className="container relative z-10">
        <div className="text-center mb-16">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
          >
            <div className="section-label mx-auto w-fit">
              <Code2 size={14} />
              Technology Stack
            </div>
          </motion.div>

          <ScrollFloat
            containerClassName="section-title"
            highlightWords={['Technologies']}
            highlightClassName="text-[var(--color-red)]"
          >
            Modern Technologies We Use
          </ScrollFloat>

          <motion.p
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ delay: 0.2 }}
            className="section-subtitle mx-auto"
          >
            We leverage industry-leading tools and frameworks to build scalable, secure, and high-performance software systems.
          </motion.p>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6 xl:gap-8 max-w-[1400px] mx-auto">
          {stackCategories.map((category, i) => {
            const Icon = category.icon;
            return (
              <motion.div
                key={category.title}
                initial={{ opacity: 0, y: 40 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ delay: i * 0.05, duration: 0.4 }}
                className="relative group h-full flex flex-col"
              >
                <div className="sketch-card p-6 h-full glitch-hover flex flex-col items-start text-left bg-[var(--color-bg-card)]">
                  <div className="flex items-center gap-4 mb-4">
                    <div className="w-12 h-12 rounded-lg border-2 border-solid border-[var(--color-sketch-border)] flex shrink-0 items-center justify-center group-hover:border-[var(--color-red)] group-hover:bg-[var(--color-red)] transition-all duration-300 bg-[var(--color-bg-secondary)]">
                      <Icon size={20} className="text-[var(--color-text)] group-hover:text-white transition-colors duration-300" />
                    </div>
                    <h3 className="text-xl font-bold font-[var(--font-heading)] text-[var(--color-text)]">
                      {category.title}
                    </h3>
                  </div>
                  
                  <p className="text-[var(--color-text-dim)] text-sm mb-6 flex-grow leading-relaxed">
                    {category.description}
                  </p>
                  
                  <div className="flex flex-wrap gap-2 w-full mt-auto">
                    {category.technologies.map(tech => (
                      <motion.div 
                        key={tech}
                        whileHover={{ scale: 1.05 }}
                        className="py-1 px-3 text-xs font-semibold rounded-full bg-[var(--color-bg-primary)] border border-[var(--color-sketch-border)] text-[var(--color-text-muted)] hover:text-[var(--color-red)] hover:border-[var(--color-red)] transition-all duration-300 cursor-default shadow-[1px_1px_0px_var(--color-sketch-shadow)] hover:shadow-[2px_2px_0px_var(--color-red)]"
                      >
                        {tech}
                      </motion.div>
                    ))}
                  </div>
                </div>
              </motion.div>
            )
          })}
        </div>
      </div>
    </section>
  )
}
